/**
 * 日志脱敏。
 *
 * 约束来源：CONTRACT.md 第 22 行 "Ticket query MUST be redacted by proxy/logging"、
 * 需求文档第十节 "日志不记录完整凭据、客户财务正文和全部页面上下文"。
 *
 * 前端同样不能把 ticket / 源 token / 接入 token 写进控制台或错误上报，
 * 因此所有"打印 URL / 会话对象"的入口都先经过这里。
 */

const SENSITIVE_QUERY_KEYS = new Set([
  'accesstoken',
  'authorization',
  'code',
  'refreshtoken',
  'sessiontoken',
  'sourcetoken',
  'ticket',
  'token',
]);

/** 把 URL 中的敏感查询参数替换为 REDACTED。非 URL 输入原样返回。 */
export function redactUrl(value: string): string {
  try {
    const url = new URL(value, 'http://placeholder.invalid');
    let touched = false;
    for (const key of [...url.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        url.searchParams.set(key, 'REDACTED');
        touched = true;
      }
    }
    if (!touched) return value;
    return value.startsWith('http') || value.startsWith('ws')
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}

/** 截断长文本，避免把整段客户消息写进日志。 */
export function truncate(text: string, limit = 120): string {
  return text.length <= limit ? text : `${text.slice(0, limit)}…`;
}

/**
 * 安全日志器：自动脱敏 URL，默认不打令牌/正文。
 * 生产构建（import.meta.env.PROD 等效）下静默。
 */
export interface SafeLogger {
  debug(message: string, detail?: Record<string, unknown>): void;
  info(message: string, detail?: Record<string, unknown>): void;
  warn(message: string, detail?: Record<string, unknown>): void;
}

export function createSafeLogger(options: { enabled: boolean; scope: string }): SafeLogger {
  const { enabled, scope } = options;
  const write = (
    level: 'debug' | 'info' | 'warn',
    message: string,
    detail?: Record<string, unknown>,
  ): void => {
    if (!enabled) return;
    const payload = detail ? sanitizeDetail(detail) : undefined;
    const line = `[${scope}] ${message}`;
    if (level === 'warn') {
      console.warn(line, payload ?? '');
    } else if (level === 'info') {
      console.info(line, payload ?? '');
    } else {
      console.debug(line, payload ?? '');
    }
  };
  return {
    debug: (message, detail) => write('debug', message, detail),
    info: (message, detail) => write('info', message, detail),
    warn: (message, detail) => write('warn', message, detail),
  };
}

const SENSITIVE_DETAIL_KEYS = new Set([
  'accesstoken',
  'authorization',
  'draft',
  'password',
  'refreshtoken',
  'session',
  'sourcetoken',
  'text',
  'ticket',
  'token',
]);

function sanitizeDetail(detail: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(detail)) {
    if (SENSITIVE_DETAIL_KEYS.has(key.toLowerCase())) {
      out[key] = 'REDACTED';
    } else if (typeof value === 'string' && /^https?:\/\/|^wss?:\/\//u.test(value)) {
      out[key] = redactUrl(value);
    } else {
      out[key] = value;
    }
  }
  return out;
}